import axios from "axios";
import { API_BASE } from "./apiBase";

const API = API_BASE;

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fileUrl(fileName) {
  return fileName ? `/uploads/${fileName}` : "";
}

function replaceShortcodes(template, data) {
  return String(template ?? "").replace(/\{\{([^}]+)\}\}/g, (_, rawKey) => {
    const key = String(rawKey || "").trim();
    return data[key] ?? `{{${key}}}`;
  });
}

function qrCodeUrl(value) {
  const text = String(value || "").trim();
  return text ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=4&data=${encodeURIComponent(text)}` : "";
}

function resultVerificationUrl(data) {
  const origin = window.location?.origin || "";
  const params = new URLSearchParams();
  if (data.registration_no) params.set("reg", data.registration_no);
  if (data.dob) params.set("dob", data.dob);
  return `${origin}/result-verification${params.toString() ? `?${params.toString()}` : ""}`;
}

function formatDateOnly(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).split(/[T ]/)[0] || String(value);
  }

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatDateSlash(value) {
  if (!value) return "";

  const raw = String(value).split(/[T ]/)[0];
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  const dmyMatch = /^(\d{2})[-/](\d{2})[-/](\d{4})$/.exec(raw);
  if (dmyMatch) {
    return `${dmyMatch[1]}/${dmyMatch[2]}/${dmyMatch[3]}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return raw;

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

const removedMarksheetFields = new Set([
  "roll_no",
  "semester_rows",
  "total_marks",
  "full_marks",
  "pass_marks",
  "grade_point",
  "footer_note",
  "project_obtained_marks",
  "project_full_marks",
  "project_pass_marks",
]);

function popupDocument(title, body, orientation = "portrait") {
  const popup = window.open("", "_blank", "width=1100,height=1000");
  if (!popup) return;

  popup.document.write(`
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          body { margin: 0; background: #f3f4f6; font-family: "Segoe UI", sans-serif; color: #0f172a; }
          .print-shell { padding: 18px; }
          .sheet { margin: 0 auto; background: #fff; box-shadow: 0 16px 40px rgba(15,23,42,.12); }
          .sheet.a4 { position: relative; overflow: hidden; }
          .sheet.a4.portrait { width: 794px; height: 1123px; }
          .sheet.a4.landscape { width: 1123px; height: 794px; }
          .sheet.card { width: 680px; min-height: 420px; position: relative; overflow: hidden; border-radius: 24px; }
          .background-image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; display: block; z-index: 0; background: #fff; }
          .logo-box { position: absolute; left: 48px; top: 34px; width: 86px; height: 86px; border-radius: 22px; overflow: hidden; border: none; background: transparent; z-index: 3; }
          .logo-box img, .signature-box img, .student-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
          .signature-box { position: absolute; right: 68px; bottom: 72px; width: 150px; height: 58px; border-radius: 12px; overflow: hidden; border: none; background: transparent; z-index: 3; }
          .student-photo { position: absolute; right: 54px; top: 148px; width: 136px; height: 164px; border-radius: 20px; overflow: hidden; border: none; background: transparent; z-index: 3; }
          .simple-hero { padding: 26px 30px; color: #fff; background: linear-gradient(135deg, #4e1b24 0%, #7c2d3a 52%, #c2410c 100%); }
          .simple-body { padding: 26px 30px; display: grid; gap: 18px; }
          .simple-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
          .simple-card { border: 1px solid rgba(148,163,184,.18); border-radius: 18px; padding: 14px; background: rgba(255,255,255,.92); }
          .field-layer { position: absolute; white-space: pre-wrap; color: #0f172a; z-index: 2; box-sizing: border-box; overflow-wrap: anywhere; word-break: break-word; }
          @media print {
            @page { size: A4 ${orientation}; margin: 0; }
            body { background: #fff; }
            .print-shell { padding: 0; }
            .sheet { box-shadow: none; }
          }
        </style>
      </head>
      <body>${body}</body>
    </html>
  `);
  popup.document.close();

  const waitForImages = () => {
    const images = Array.from(popup.document.images || []);
    return Promise.all(
      images.map((image) => {
        if (image.complete && image.naturalWidth !== 0) return Promise.resolve();
        return new Promise((resolve) => {
          image.onload = resolve;
          image.onerror = resolve;
        });
      })
    );
  };

  const waitForFonts = popup.document.fonts?.ready || Promise.resolve();
  Promise.all([waitForImages(), waitForFonts])
    .then(() => {
      popup.requestAnimationFrame(() => {
        window.setTimeout(() => {
          popup.focus();
          popup.print();
        }, 350);
      });
    })
    .catch(() => {
      window.setTimeout(() => {
        popup.focus();
        popup.print();
      }, 700);
    });
}

export async function fetchAmciDesign(documentType) {
  try {
    const res = await axios.get(`${API}/amci-designs/${documentType}`);
    return res.data || null;
  } catch (error) {
    return null;
  }
}

export async function fetchGradePoints() {
  try {
    const res = await axios.get(`${API}/grade-points`);
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    return [];
  }
}

export function resolveGradePointByPercentage(percentage, gradeRows) {
  const value = Number(percentage || 0);
  const rows = Array.isArray(gradeRows) ? gradeRows : [];
  const match = rows.find((row) => value >= Number(row.percent_from || 0) && value <= Number(row.percent_to || 0));

  return {
    grade: match?.grade_name || `${value.toFixed(2)}%`,
    grade_point: match ? Number(match.grade_point || 0).toFixed(2) : "",
  };
}

function buildSimpleDocumentHtml(documentType, design, data) {
  const form = design?.form || {};
  const uploads = form.uploads || {};
  const isIdCard = documentType === "idcard";
  const backgroundPreview = uploads[Object.keys(uploads)[0]] || "";
  const uploadKeys = Object.keys(uploads);
  const backBackgroundPreview = isIdCard ? uploads[uploadKeys[1]] || "" : "";
  const logoPreview = isIdCard ? uploads[uploadKeys[2]] || "" : uploads[uploadKeys[1]] || "";
  const signaturePreview = isIdCard ? "" : uploads[uploadKeys[2]] || "";
  const title = replaceShortcodes(form.headerTitle || (documentType === "admit" ? "Student Admit Card" : "Student ID Card"), data);
  const subtitle = replaceShortcodes(form.subTitle || "National Institute of Digital Careers and Skills", data);
  const bodyText = replaceShortcodes(
    form.bodyText ||
      (documentType === "admit"
        ? "Student: {{student_name}}\nRegistration No: {{registration_no}}\nCourse: {{course_name}}\nSession: {{session_year}}\nStudy Centre: {{study_centre}}\nStatus: {{status}}"
        : "Student: {{student_name}}\nRegistration No: {{registration_no}}\nCourse: {{course_name}}\nValid Upto: {{valid_upto}}\nContact: {{student_mobile}}\nStudy Centre: {{study_centre}}"),
    data
  );
  const footerNote = replaceShortcodes(form.footerNote || "Active AMCI design", data);
  const nameText = replaceShortcodes(form.studentNameCode || "{{student_name}}", data);
  const backgroundStyle = backgroundPreview
    ? "background:#fff;"
    : "background: linear-gradient(135deg, #fff7ed 0%, #ffffff 44%, #eff6ff 100%);";

  return `
    <div class="print-shell">
      <div class="sheet card" style="${backgroundStyle}">
        ${backgroundPreview ? `<img class="background-image" src="${escapeHtml(backgroundPreview)}" alt="" />` : ""}
        <div class="simple-hero" style="position:relative;z-index:1;">
          <h1 style="margin:0 0 8px;">${escapeHtml(title)}</h1>
          <p style="margin:0;opacity:.82;">${escapeHtml(subtitle)}</p>
        </div>
        <div class="simple-body" style="position:relative;z-index:1;">
          <div style="display:flex;justify-content:space-between;gap:18px;align-items:flex-start;flex-wrap:wrap;">
            <div style="flex:1 1 340px;">
              <div style="font-size:28px;font-weight:800;color:#7c2d3a;margin-bottom:12px;">${escapeHtml(nameText)}</div>
              <div style="line-height:1.85;color:#334155;">${escapeHtml(bodyText).replace(/\n/g, "<br/>")}</div>
            </div>
            <div style="display:grid;gap:12px;">
              ${logoPreview ? `<div style="width:86px;height:86px;border-radius:20px;overflow:hidden;border:1px solid rgba(148,163,184,.18);background:#fff;"><img src="${logoPreview}" alt="Logo" /></div>` : ""}
              ${data.student_photo ? `<div style="width:136px;height:164px;border-radius:20px;overflow:hidden;border:1px solid rgba(148,163,184,.18);background:#fff;"><img src="${data.student_photo}" alt="Student" /></div>` : ""}
            </div>
          </div>
          <div class="simple-grid">
            <div class="simple-card"><strong>Registration No</strong><div>${escapeHtml(data.registration_no)}</div></div>
            <div class="simple-card"><strong>Course</strong><div>${escapeHtml(data.course_name)}</div></div>
            <div class="simple-card"><strong>Session</strong><div>${escapeHtml(data.session_year)}</div></div>
            <div class="simple-card"><strong>Study Centre</strong><div>${escapeHtml(data.study_centre)}</div></div>
          </div>
          <div style="display:flex;justify-content:space-between;gap:12px;align-items:end;">
            <div style="color:#64748b;font-size:13px;">${escapeHtml(footerNote)}</div>
            ${signaturePreview ? `<div style="width:130px;height:52px;border-radius:12px;overflow:hidden;border:1px solid rgba(148,163,184,.16);background:#fff;"><img src="${signaturePreview}" alt="Signature" /></div>` : ""}
          </div>
        </div>
      </div>
      ${
        isIdCard && backBackgroundPreview
          ? `<div class="sheet card" style="background:#fff;margin-top:18px;"><img class="background-image" src="${escapeHtml(backBackgroundPreview)}" alt="" /></div>`
          : ""
      }
    </div>
  `;
}

function buildStructuredDocumentHtml(documentType, design, data) {
  const form = design?.form || {};
  const previewFields = design?.preview_fields || {};
  const fieldPositions = design?.field_positions || {};
  const uploads = form.uploads || {};
  const fieldStyleMap = form.certificateFieldStyleMap || {};
  const imageLayout = form.certificateImageLayout || {};
  const getUploadPreview = (...keys) => {
    for (const key of keys) {
      if (key && uploads[key]) return uploads[key];
    }
    return "";
  };
  const isFranchiseAffiliatedMode = Boolean(
    uploads["Header Image"] ||
    uploads["Franchise Authority Signature"] ||
    uploads["NIDCS Administrator Signature"]
  );
  const backgroundPreview = getUploadPreview(Object.keys(uploads)[0], "Certificate Background", "Header Image");
  const logoPreview = isFranchiseAffiliatedMode ? "" : getUploadPreview(Object.keys(uploads)[1], "Institute Logo");
  const signaturePreview = getUploadPreview(Object.keys(uploads)[2], "Chairman Signature", "NIDCS Administrator Signature");
  const authoritySignaturePreview = getUploadPreview("Franchise Authority Signature");
  const adminSignaturePreview = getUploadPreview("NIDCS Administrator Signature");
  const bodyStyle = form.certificateBodyStyle || {};
  const borderStyle = form.certificateBorderStyle || {};
  const isCertificate = documentType === "certificate";
  const toFiniteNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };
  const PREVIEW_W = 620;
  const PREVIEW_H = 877;
  const PRINT_W = 794;
  const PRINT_H = 1123;
  const SCALE = PRINT_W / PREVIEW_W;
  const sp = (value) => toFiniteNumber(value, 0) * SCALE;
  const borderPadding = Math.max(0, Number(borderStyle.padding ?? borderStyle.inset ?? 0));
  const borderMargin = Math.max(0, Number(borderStyle.margin ?? borderStyle.bottomMargin ?? 0));
  const innerEnabled = Boolean(borderStyle.innerEnabled);
  const innerWidth = Math.max(0, Number(borderStyle.innerWidth || 0));
  const innerGap = Math.max(0, Number(borderStyle.innerGap || 0));
  const borderInsetBase = isCertificate ? borderMargin : 0;
  const contentInsetBase = isCertificate
    ? borderMargin + borderPadding + (innerEnabled ? innerGap + innerWidth : 0)
    : 0;
  const borderInset = sp(borderInsetBase);
  const contentInset = sp(contentInsetBase);
  const certificateBodyWidth = Number(bodyStyle.bodyWidth || 760);
  const hasStructuredBackgroundImage = Boolean(backgroundPreview);
  const backgroundStyle = hasStructuredBackgroundImage
    ? "background:#fff;"
    : "background: linear-gradient(135deg, #fffaf5 0%, #ffffff 45%, #f8fafc 100%);";
  const certificateBorderCss = "";
  const canvasW = PRINT_W - contentInset * 2;
  const canvasH = PRINT_H - contentInset * 2;

  const fieldHtml = Object.entries(previewFields)
    .map(([key, value]) => {
      if (isCertificate && key === "verification_qr") return "";
      if (documentType === "marksheet" && key === "title") return "";
      if (documentType === "marksheet" && removedMarksheetFields.has(key)) return "";

      const position = fieldPositions[key];
      if (!position) return "";

      const renderedValue = replaceShortcodes(value, data);
      const isTitle = key === "title";
      const isFooter = key === "footer_note";
      const isModuleDetails = key === "module_details";
      const isRows = key === "semester_rows" || isModuleDetails;
      const isPhoto = key === "student_photo";
      const isQrCode = key === "qr_code";
      const isBody = key === "body_text";
      const isMarksheetCentreCode = documentType === "marksheet" && key === "centre_code";

      const fieldStyle = fieldStyleMap[key] || {};
      const textColor = fieldStyle.color || position.color || (isFooter ? "#475569" : "#0f172a");
      const textAlign = fieldStyle.align || (isTitle ? "center" : "left");
      const fontSize = sp(Number(fieldStyle.fontSize || (isTitle ? 34 : isBody ? Number(bodyStyle.fontSize || 20) : isFooter ? 16 : isModuleDetails ? 13 : isMarksheetCentreCode ? 14 : 20)));
      const fontWeight = fieldStyle.bold ? 800 : (isTitle ? 800 : isRows ? 600 : 700);
      const fontStyle = fieldStyle.italic ? "italic" : "normal";
      const textDecoration = fieldStyle.underline ? "underline" : "none";
      const fieldLineHeight = Number(fieldStyle.lineHeight || (isModuleDetails ? 1.45 : isRows || isBody ? 1.8 : 1.5));
      const baseMaxWidth = isRows ? 610 : isBody ? (isCertificate ? certificateBodyWidth : 560) : isTitle ? (isCertificate ? 700 : 460) : (isCertificate ? 680 : 520);
      const rawX = sp(Number(position.x || 0));
      const rawY = sp(Number(position.y || 0));

      if (isPhoto) {
        if (!data.student_photo) return "";
        const photoReqW = Math.max(sp(40), sp(Number(fieldStyle.width || 118)));
        const photoReqH = Math.max(sp(40), sp(Number(fieldStyle.height || 138)));
        const photoW = Math.max(sp(40), Math.min(photoReqW, canvasW));
        const photoH = Math.max(sp(40), Math.min(photoReqH, canvasH));
        const safeX = Math.max(0, Math.min(canvasW - photoW, rawX));
        const safeY = Math.max(0, Math.min(canvasH - photoH, rawY));
        return `
          <div
            class="field-layer"
            style="
              left:${safeX}px;
              top:${safeY}px;
              width:${photoW}px;
              height:${photoH}px;
              border:none;
              background:transparent;
              overflow:hidden;
              z-index:3;
            "
          ><img src="${escapeHtml(data.student_photo)}" alt="Student Photo" style="width:100%;height:100%;object-fit:contain;display:block;background:#fff;" /></div>
        `;
      }

      if (isQrCode) {
        const qrSrc = qrCodeUrl(data.qr_code || data.registration_no || data.certificate_no);
        if (!qrSrc) return "";
        const qrReqW = Math.max(sp(40), sp(Number(fieldStyle.width || 118)));
        const qrReqH = Math.max(sp(40), sp(Number(fieldStyle.height || 118)));
        const qrW = Math.max(sp(40), Math.min(qrReqW, canvasW));
        const qrH = Math.max(sp(40), Math.min(qrReqH, canvasH));
        const safeX = Math.max(0, Math.min(canvasW - qrW, rawX));
        const safeY = Math.max(0, Math.min(canvasH - qrH, rawY));
        return `
          <div
            class="field-layer"
            style="
              left:${safeX}px;
              top:${safeY}px;
              width:${qrW}px;
              height:${qrH}px;
              background:transparent;
              overflow:hidden;
              z-index:3;
            "
          ><img src="${escapeHtml(qrSrc)}" alt="QR Code" style="width:100%;height:100%;object-fit:contain;display:block;" /></div>
        `;
      }

      const reqW = Math.max(sp(40), sp(Number(fieldStyle.width || baseMaxWidth)));
      const reqH = Math.max(sp(30), sp(Number(fieldStyle.height || 40)));
      const safeW = Math.max(sp(40), Math.min(reqW, canvasW));
      const safeH = Math.max(sp(30), Math.min(reqH, canvasH));
      const safeX = Math.max(0, Math.min(canvasW - safeW, rawX));
      const safeY = Math.max(0, Math.min(canvasH - safeH, rawY));
      const maxHeight = Math.max(sp(30), canvasH - safeY);

      return `
        <div
          class="field-layer"
          style="
            left:${safeX}px;
            top:${safeY}px;
            font-weight:${fontWeight};
            font-size:${fontSize}px;
            color:${textColor};
            line-height:${fieldLineHeight};
            max-width:${safeW}px;
            width:${safeW}px;
            max-height:${maxHeight}px;
            overflow:hidden;
            text-align:${textAlign};
            letter-spacing:${isTitle ? ".03em" : "normal"};
            font-family:${fieldStyle.fontFamily || (isBody ? bodyStyle.fontFamily || "Georgia, serif" : "inherit")};
            font-style:${fontStyle};
            text-decoration:${textDecoration};
            ${isBody ? "z-index:2;" : ""}
          "
        >${isBody ? renderedValue : escapeHtml(renderedValue).replace(/\n/g, "<br/>")}</div>
      `;
    })
    .join("");

  const headerImageLayout = imageLayout.headerImage || { x: 150, y: 48, width: 320, height: 110, fit: "contain", cropX: 50, cropY: 50 };
  const authorityLayout = imageLayout.authoritySignature || { x: 70, y: 746, width: 150, height: 58, fit: "contain", cropX: 50, cropY: 50 };
  const adminLayout = imageLayout.adminSignature || { x: 400, y: 746, width: 150, height: 58, fit: "contain", cropX: 50, cropY: 50 };
  const headerW = Math.max(sp(80), Math.min(sp(toFiniteNumber(headerImageLayout.width, 320)), canvasW));
  const headerH = Math.max(sp(50), Math.min(sp(toFiniteNumber(headerImageLayout.height, 110)), canvasH));
  const headerX = Math.max(0, Math.min(canvasW - headerW, sp(toFiniteNumber(headerImageLayout.x, 150))));
  const headerY = Math.max(0, Math.min(canvasH - headerH, sp(toFiniteNumber(headerImageLayout.y, 48))));
  const headerCropX = Math.max(0, Math.min(100, toFiniteNumber(headerImageLayout.cropX, 50)));
  const headerCropY = Math.max(0, Math.min(100, toFiniteNumber(headerImageLayout.cropY, 50)));

  const authW = Math.max(sp(60), Math.min(sp(toFiniteNumber(authorityLayout.width, 150)), canvasW));
  const authH = Math.max(sp(30), Math.min(sp(toFiniteNumber(authorityLayout.height, 58)), canvasH));
  const authX = Math.max(0, Math.min(canvasW - authW, sp(toFiniteNumber(authorityLayout.x, 70))));
  const authY = Math.max(0, Math.min(canvasH - authH, sp(toFiniteNumber(authorityLayout.y, 746))));
  const authCropX = Math.max(0, Math.min(100, toFiniteNumber(authorityLayout.cropX, 50)));
  const authCropY = Math.max(0, Math.min(100, toFiniteNumber(authorityLayout.cropY, 50)));

  const adminW = Math.max(sp(60), Math.min(sp(toFiniteNumber(adminLayout.width, 150)), canvasW));
  const adminH = Math.max(sp(30), Math.min(sp(toFiniteNumber(adminLayout.height, 58)), canvasH));
  const adminX = Math.max(0, Math.min(canvasW - adminW, sp(toFiniteNumber(adminLayout.x, 400))));
  const adminY = Math.max(0, Math.min(canvasH - adminH, sp(toFiniteNumber(adminLayout.y, 746))));
  const adminCropX = Math.max(0, Math.min(100, toFiniteNumber(adminLayout.cropX, 50)));
  const adminCropY = Math.max(0, Math.min(100, toFiniteNumber(adminLayout.cropY, 50)));

  return `
    <div class="print-shell">
      <div class="sheet a4 portrait" style="${backgroundStyle}${certificateBorderCss}">
        ${isCertificate && borderStyle.enabled ? `<div style="position:absolute;inset:${borderInset}px;border:${Math.max(0, Number(borderStyle.width || 0))}px ${borderStyle.style || "solid"} ${borderStyle.color || "#0f172a"};border-radius:${Math.max(0, Number(borderStyle.radius || 0))}px;box-sizing:border-box;z-index:1;pointer-events:none;"></div>` : ""}
        ${isCertificate && borderStyle.enabled && borderStyle.innerEnabled ? `<div style="position:absolute;inset:${borderInset + sp(innerGap)}px;border:${sp(Math.max(0, Number(borderStyle.innerWidth || 0)))}px ${borderStyle.innerStyle || "solid"} ${borderStyle.innerColor || "#7c2d3a"};border-radius:${sp(Math.max(0, Number(borderStyle.innerRadius || 0)))}px;box-sizing:border-box;z-index:1;pointer-events:none;"></div>` : ""}
        ${hasStructuredBackgroundImage && !isFranchiseAffiliatedMode ? `<img class="background-image" src="${escapeHtml(backgroundPreview)}" alt="" style="inset:${contentInset}px;" />` : ""}
        ${hasStructuredBackgroundImage && isFranchiseAffiliatedMode ? `
          <div style="
            position:absolute;
            left:${headerX + contentInset}px;
            top:${headerY + contentInset}px;
            width:${headerW}px;
            height:${headerH}px;
            border-radius:10px;
            overflow:hidden;
            border:none;
            background:#fff;
            z-index:4;
          ">
            <img src="${escapeHtml(backgroundPreview)}" alt="" style="width:100%;height:100%;object-fit:${headerImageLayout.fit === "cover" ? "cover" : "contain"};object-position:${headerCropX}% ${headerCropY}%;display:block;background:#fff;" />
          </div>
        ` : ""}
        ${logoPreview ? `<div class="logo-box" style="left:${48 + contentInset}px;top:${34 + contentInset}px;"><img src="${logoPreview}" alt="Logo" /></div>` : ""}
        ${data.student_photo && !isCertificate && !previewFields.student_photo ? `<div class="student-photo"><img src="${data.student_photo}" alt="Student" /></div>` : ""}
        <div style="position:absolute;inset:${contentInset}px;z-index:2;">${fieldHtml}</div>
        ${!isFranchiseAffiliatedMode && signaturePreview ? `<div class="signature-box" style="right:${68 + contentInset}px;bottom:${72 + contentInset}px;"><img src="${signaturePreview}" alt="Signature" /></div>` : ""}
        ${isFranchiseAffiliatedMode && authoritySignaturePreview ? `
          <div style="
            position:absolute;
            left:${authX + contentInset}px;
            top:${authY + contentInset}px;
            width:${authW}px;
            height:${authH}px;
            border-radius:12px;
            overflow:hidden;
            border:none;
            background:#fff;
            z-index:4;
          ">
            <img src="${escapeHtml(authoritySignaturePreview)}" alt="" style="width:100%;height:100%;object-fit:${authorityLayout.fit === "cover" ? "cover" : "contain"};object-position:${authCropX}% ${authCropY}%;display:block;background:#fff;" />
          </div>
        ` : ""}
        ${isFranchiseAffiliatedMode && adminSignaturePreview ? `
          <div style="
            position:absolute;
            left:${adminX + contentInset}px;
            top:${adminY + contentInset}px;
            width:${adminW}px;
            height:${adminH}px;
            border-radius:12px;
            overflow:hidden;
            border:none;
            background:#fff;
            z-index:4;
          ">
            <img src="${escapeHtml(adminSignaturePreview)}" alt="" style="width:100%;height:100%;object-fit:${adminLayout.fit === "cover" ? "cover" : "contain"};object-position:${adminCropX}% ${adminCropY}%;display:block;background:#fff;" />
          </div>
        ` : ""}
      </div>
    </div>
  `;
}

export function printAmciDocument(documentType, design, rawData) {
  const data = {
    student_name: "",
    father_name: "",
    registration_no: "",
    course_name: "",
    admission_date: "",
    course_completion_date: "",
    period_of_course: "",
    exam_date: "",
    exam_time: "",
    study_centre: "",
    centre_name: "",
    centre_address: "",
    session_year: "",
    grade: "",
    grade_point: "",
    certificate_no: "",
    issue_date: "",
    verification_qr: "Verification QR",
    qr_code: "",
    valid_upto: "",
    student_mobile: "",
    centre_code: "",
    barcode_value: "",
    total_obtained: "",
    total_full_marks: "",
    total_pass_marks: "",
    obtained_marks: "",
    theory_obtained_marks: "",
    theory_full_marks: "",
    theory_pass_marks: "",
    practical_obtained_marks: "",
    practical_full_marks: "",
    practical_pass_marks: "",
    project_assignment_obtained_marks: "",
    project_assignment_full_marks: "",
    project_assignment_pass_marks: "",
    viva_obtained_marks: "",
    viva_full_marks: "",
    viva_pass_marks: "",
    percentage: "",
    module_details: "",
    status: "",
    ...rawData,
  };
  data.issue_date = formatDateOnly(data.issue_date);
  data.admission_date = formatDateSlash(data.admission_date);
  data.course_completion_date = formatDateSlash(data.course_completion_date);
  if (!data.period_of_course && data.admission_date && data.course_completion_date) {
    data.period_of_course = `${data.admission_date} to ${data.course_completion_date}`;
  }
  if (!data.centre_name) {
    data.centre_name = data.study_centre;
  }
  if (!data.qr_code) {
    data.qr_code = resultVerificationUrl(data);
  }

  const body =
    documentType === "marksheet" || documentType === "certificate"
      ? buildStructuredDocumentHtml(documentType, design, data)
      : buildSimpleDocumentHtml(documentType, design, data);

  popupDocument(documentType.toUpperCase(), body, "portrait");
}

export function buildMarksheetRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => `${row.module_title}: ${row.marks_obtained}/${row.max_marks} (${row.approval_status || "approved"})`)
    .join("\n");
}

export function buildModuleDetailsRows(rows) {
  const groups = (Array.isArray(rows) ? rows : []).reduce((accumulator, row) => {
    const semesterNo = Number(row?.semester_no || 1);
    const title = row?.semester_title || `Semester ${semesterNo}`;
    if (!accumulator[semesterNo]) {
      accumulator[semesterNo] = {
        semesterNo,
        title,
        modules: [],
      };
    }
    if (row?.module_title) {
      accumulator[semesterNo].modules.push(row.module_title);
    }
    return accumulator;
  }, {});

  return Object.values(groups)
    .sort((left, right) => left.semesterNo - right.semesterNo)
    .map((group) => `${group.title}: ${group.modules.join(", ")}`)
    .join("\n");
}

export function calculatePassMarks(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    const explicitPassMarks = row?.pass_marks ?? row?.pass_mark ?? row?.minimum_marks;
    const value = explicitPassMarks === undefined || explicitPassMarks === null || explicitPassMarks === ""
      ? Number(row?.max_marks || 0) * 0.3
      : Number(explicitPassMarks || 0);
    return sum + value;
  }, 0);
}

export function sumMarksField(rows, key) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + Number(row?.[key] || 0), 0);
}

function sumFirstAvailableMarksField(rows, keys) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    const value = keys.map((key) => row?.[key]).find((item) => item !== undefined && item !== null && item !== "");
    return sum + Number(value || 0);
  }, 0);
}

export function buildMarksSummary(rows) {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const theoryObtained = sumMarksField(sourceRows, "marks_obtained");
  const theoryFull = sumMarksField(sourceRows, "max_marks");
  const theoryPass = sumMarksField(sourceRows, "pass_marks");
  const practicalObtained = sumMarksField(sourceRows, "practical_marks_obtained");
  const practicalFull = sumMarksField(sourceRows, "practical_max_marks");
  const practicalPass = sumMarksField(sourceRows, "practical_pass_marks");
  const projectObtained = sumFirstAvailableMarksField(sourceRows, ["project_marks_obtained", "project_assignment_obtained_marks"]);
  const projectFull = sumFirstAvailableMarksField(sourceRows, ["project_max_marks", "project_assignment_full_marks"]);
  const projectPass = sumFirstAvailableMarksField(sourceRows, ["project_pass_marks", "project_assignment_pass_marks"]);
  const vivaObtained = sumMarksField(sourceRows, "viva_marks_obtained");
  const vivaFull = sumMarksField(sourceRows, "viva_max_marks");
  const vivaPass = sumMarksField(sourceRows, "viva_pass_marks");
  const totalObtained = theoryObtained + practicalObtained + projectObtained + vivaObtained;
  const totalFull = theoryFull + practicalFull + projectFull + vivaFull;
  const totalPass = theoryPass + practicalPass + projectPass + vivaPass;
  const percentage = totalFull > 0 ? ((totalObtained / totalFull) * 100).toFixed(2) : "0.00";

  return {
    theoryObtained,
    theoryFull,
    theoryPass,
    practicalObtained,
    practicalFull,
    practicalPass,
    projectObtained,
    projectFull,
    projectPass,
    vivaObtained,
    vivaFull,
    vivaPass,
    totalObtained,
    totalFull,
    totalPass,
    percentage,
  };
}

export function resolveStudentPhoto(row) {
  if (row?.photo?.startsWith?.("data:") || row?.photo?.startsWith?.("http")) {
    return row.photo;
  }

  return fileUrl(row?.photo);
}



